import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, Inject, Input, PLATFORM_ID, inject } from '@angular/core';
import type { FormGroup } from '@angular/forms';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TrackElementDirective } from '@core-mkt/directives/track-element/track-element.directive';
import { CookieService } from '@core-mkt/services/cookie/cookie.service';
import { ElementTrackingService } from '@core-mkt/services/element-tracking/element-tracking.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { IterableService } from '@core-mkt/services/iterable/iterable.service';
import { PricingService } from '@core-mkt/services/pricing/pricing.service';
import { PhoneValidationService } from '@core-mkt/services/phone-validation/phone-validation.service';
import { StorageService } from '@core-mkt/services/storage/storage.service';
import { MarketingSyncUserService } from '@core-mkt/services/sync-user/marketing-sync-user.service';
import { VerticalService } from '@core-mkt/services/vertical/vertical.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { FormPayload } from '@core-mkt/shared/components/email-form/email-form/email-form';
import { Subject, fromEvent } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import { PhoneCapture } from './phone-capture';

@Component({
  selector: 'phone-capture',
  templateUrl: './phone-capture.component.html',
  styleUrls: ['./phone-capture.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TrackElementDirective],
})
export class PhoneCaptureComponent implements OnInit, OnDestroy {
  @Input() data: PhoneCapture;
  @Input() stickyPhoneOverride?: number;
  phoneForm: FormGroup;
  isSubmited = false;
  isPhoneInvalid = false;
  window: Window | null;
  buttonLabelDefault = 'Get Text Updates';
  couponDefault = 'Phone20';
  display: string;
  displayConfetti = false;
  displayAfterSubmit: string;
  dismissedString = 'phoneDismissed';
  path: string;
  showPhoneCapture = false;
  ambassador: boolean;
  isLoggedIn = false;
  phoneMinimized = false;
  useInverseSlideup = false;
  isMinimized = false;
  isMobile = false;

  // Modal-first variant (displayMode === 'modalThenSlideUp')
  modalActive = false;
  modalDismissed = false;
  suppressTransition = false;
  now: number;
  saleActive = false;

  hasUrgencyData = false;
  effectivePercentage = 20;
  effectiveCountdownDays = 3;
  urgencyMessage = '';
  phoneCaptureContentCached = '';

  // SMS consent disclosure values, resolved once in ngOnInit. Read from EnvService (which deep-clones
  // the environment on every `.get`), so they are cached here rather than computed in template-bound getters.
  brandName = '';
  termsOfServiceUrl = '';
  privacyPolicyUrl = '';
  // Cached brand id — reused by hot paths (template-bound getters, scroll-gated setPhoneCapture,
  // cookie/storage keys, submit payloads) so we never hit this.env.get (deep-clone) on scroll.
  brandId = '';

  // EPS-4414: HSP intentionally has no entry yet — same as AARP/IDS today; the null-check
  // where this map is read already handles an unconfigured brand gracefully. A real
  // Iterable list ID needs to be backfilled by EPS-4410's config-values work. See concerns.md.
  private listIds = {
    AA: 94171,
    ACE: 5388805,
    DEC: 5039154,
    INS: 5388813,
    MTG: 5388818,
    QA: 7054980,
  };
  private submissionDate: Date | null = null;
  private isBrowser: boolean;

  private readonly phoneValidation = inject(PhoneValidationService);
  private readonly formBuilder = inject(FormBuilder);
  private document = inject(DOCUMENT);
  private redactor = inject(WysiwygRedactorService);
  private iterable = inject(IterableService);
  private readonly storageService = inject(StorageService);
  private env = inject(EnvService);
  private pricingService = inject(PricingService);
  private route = inject(ActivatedRoute);
  private breakpointObserver = inject(BreakpointObserver);
  private cookie = inject(CookieService);
  private elementTracking = inject(ElementTrackingService);
  private readonly marketingSyncUserService = inject(MarketingSyncUserService);
  private readonly vertical = inject(VerticalService);
  private readonly destroy$ = new Subject<void>();

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.phoneForm = this.formBuilder.group({
      phoneNumber: ['', [Validators.required, this.phoneValidation.phoneValidator()]],
    });
    this.window = this.document.defaultView;
  }

  get isModalMode(): boolean {
    return this.data?.displayMode === 'modalThenSlideUp';
  }

  get displayContent(): string {
    return this.display;
  }

  get phoneError(): string {
    return 'Please enter a valid phone number';
  }

  get phoneCaptureTitle(): string {
    return 'Get Exclusive Deals via Text!';
  }

  get phoneCaptureContent(): string {
    return this.phoneCaptureContentCached;
  }

  private updatePhoneCaptureContent(): void {
    if (!this.isSubmited && !this.displayContent) {
      this.phoneCaptureContentCached =
        "Drop your phone number and we'll text you the latest deals. There's never been a better time to launch your new career.";
    } else if (this.isSubmited) {
      let content = this.displayContentAfterSubmit || "Thanks! We'll text you soon.";

      if (this.hasUrgencyData && this.urgencyMessage) {
        content += this.urgencyMessage;
      }

      this.phoneCaptureContentCached = content;
    } else {
      this.phoneCaptureContentCached = '';
    }
  }

  get displayContentAfterSubmit(): string {
    return this.data?.displayContentAfterSubmit || '';
  }

  get buttonLabel(): string {
    return this.data?.buttonLabel || this.buttonLabelDefault;
  }

  /** Coupon written to the dedicated `phoneCoupon` cookie on submit; defaults to `Phone20`. */
  get coupon(): string {
    // Trim so stray Craft whitespace can't produce a coupon code that won't match
    // downstream (mirrors how CouponAggregationService trims cookie values).
    return this.data?.coupon?.trim() || this.couponDefault;
  }

  get saleStart(): Date {
    return this.data?.saleStart;
  }

  get saleEnd(): Date {
    return this.data?.saleEnd;
  }

  get percentageOff(): number | undefined {
    return this.data?.percentage;
  }

  get daysToExpire(): number | undefined {
    return this.data?.countdownDays;
  }

  private computeUrgencyValues(): void {
    this.hasUrgencyData =
      (this.data?.percentage !== undefined && this.data?.percentage !== null) ||
      (this.data?.countdownDays !== undefined && this.data?.countdownDays !== null);

    // Coerce to Number so CMS string values can't leak into [innerHTML] urgencyMessage.
    this.effectivePercentage = Number(this.data?.percentage ?? 20);
    this.effectiveCountdownDays = Number(this.data?.countdownDays ?? 3);

    this.updateUrgencyMessage();
  }

  private updateUrgencyMessage(): void {
    if (!this.isSubmited || !this.hasUrgencyData || !this.expirationDate) {
      this.urgencyMessage = '';
    } else {
      const daysUntilExpiration = Math.ceil(
        (this.expirationDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
      );

      if (daysUntilExpiration > 0) {
        const expirationDateString = this.expirationDate?.toLocaleDateString('en-US', {
          year: '2-digit',
          month: 'numeric',
          day: 'numeric',
        });
        this.urgencyMessage = `<p><strong>Hurry, this ${this.effectivePercentage}% off deal ends ${expirationDateString}</strong></p>`;
      } else {
        this.urgencyMessage = `<p><strong>This offer has expired.</strong></p>`;
      }
    }

    this.updatePhoneCaptureContent();
  }

  get expirationDate(): Date | null {
    if (!this.submissionDate) {
      return null;
    }
    const expiration = new Date(this.submissionDate);
    expiration.setDate(expiration.getDate() + this.effectiveCountdownDays);
    return expiration;
  }

  get submissionDateFromCookie(): Date | null {
    const submissionDateStr = this.cookie.getCookie('phoneSubmissionDate');
    if (!submissionDateStr) return null;
    const parsed = new Date(submissionDateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  get phoneListInfo(): { listId: number; type: string } | null {
    const brandId = this.brandId as keyof typeof this.listIds;
    const listId = this.listIds[brandId];

    if (!listId) return null;

    return {
      listId,
      type: brandId === 'AA' ? 'RE' : brandId,
    };
  }

  ngOnInit(): void {
    // Single env read: brand config is static for the page lifetime, so resolve everything we need here
    // instead of calling this.env.get (which deep-clones) repeatedly.
    const brandConfig = this.env.get.brandConfig;
    this.brandId = brandConfig.id ?? '';
    // EPS-4414: HSP is deliberately NOT added to this list (AC8 decision) — it should render
    // with ACE-family slide-up behavior, not the AA/INS/MTG inverse variant. See concerns.md.
    this.useInverseSlideup = ['AA', 'INS', 'MTG'].includes(this.brandId);
    this.brandName = brandConfig.name ?? '';
    // Fall back to the brand homepage so the consent links point somewhere sensible instead of
    // navigating to the current page. brandConfig.url is stored protocol-less (e.g. "www.aceable.com").
    // If the brand has no url either (not the case for any brand that renders the capture today),
    // these stay empty — an accepted edge case rather than a guaranteed non-empty href.
    const homepage = this.toAbsoluteUrl(brandConfig.url);
    this.termsOfServiceUrl = brandConfig.termsOfServiceUrl || homepage;
    this.privacyPolicyUrl = brandConfig.privacyPolicyUrl || homepage;
    // Modal-first variant always opens fully — its minimized state is never persisted page-to-page,
    // so ignore the persisted phoneMinimize cookie.
    if (this.isModalMode) {
      this.isMinimized = false;
    } else {
      this.getPhoneMinimizeCookie();
    }
    const amb = this.route.snapshot.queryParamMap?.get('campaignid');
    this.now = new Date().getTime();
    if (amb === '33113') {
      this.ambassador = true;
    }
    this.path = this.vertical.getVerticalInfo().vertical;
    this.display = this.redactor.bypassSanitizer(this.data?.displayContent);
    this.displayAfterSubmit = this.redactor.bypassSanitizer(this.data?.displayContentAfterSubmit);

    if (this.data?.saleStart) {
      this.data.saleStart = new Date(this.data.saleStart);
    }
    if (this.data?.saleEnd) {
      this.data.saleEnd = new Date(this.data.saleEnd);
    }

    // Phone capture is the active capture on this page (it only mounts when email capture
    // is off) — clear any stale email-capture coupon so only the phone coupon rides onto
    // checkout links.
    if (this.data?.showStickyPhoneCapture) {
      this.cookie.deleteCookie('emailCoupon');
    }

    // Order matters: rehydrate submissionDate from the cookie and compute urgency state BEFORE running
    // checkAndCleanExpiredSubmission — its guard reads both fields and would no-op on init otherwise.
    if (this.getPhoneSubmittedCookie()?.toString()) {
      this.isSubmited = true;
      this.submissionDate = this.submissionDateFromCookie || new Date();
    }

    this.computeUrgencyValues();
    this.checkAndCleanExpiredSubmission();
    this.updatePhoneCaptureContent();

    // Re-broadcast the coupon on rehydrated (still-valid) submissions so pricing/checkout
    // components pick it up. Runs after the expiry cleanup so an expired coupon isn't re-fired.
    // Gated on showStickyPhoneCapture: this component also mounts when phone capture is toggled
    // off (it renders whenever email capture is off), and we must not emit the phone coupon there.
    if (this.isSubmited && this.data?.showStickyPhoneCapture) {
      this.pricingService.sendCouponEvent(this.coupon);
    }

    // Short-circuit: if the Craft toggle is off, no need to wire up scroll/auth subscriptions.
    if (!this.data?.showStickyPhoneCapture) {
      return;
    }

    if (this.isBrowser) {
      this.breakpointObserver
        .observe(['(max-width: 767px)'])
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          // Mirror the BreakpointObserver match state so resizing back to desktop clears isMobile.
          this.isMobile = result.matches;
        });

      if (this.window) {
        fromEvent(this.window, 'scroll')
          .pipe(auditTime(100), takeUntil(this.destroy$))
          .subscribe(() => this.handleScroll());
      }
    }

    this.setPhoneCapture(undefined, this.stickyPhoneOverride);

    this.marketingSyncUserService.syncUserReady$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      if (response?.success && response.data?.isAuthenticated) {
        this.isLoggedIn = true;
        this.setPhoneCapture(undefined, this.stickyPhoneOverride);
      }
    });
  }

  public getPhoneSubmittedCookie(): string | null {
    return this.cookie.getCookie('phoneCaptureSubmitted');
  }

  public setPhoneSubmittedCookie(): void {
    this.cookie.setCookie('phoneCaptureSubmitted', 'true');
  }

  public setPhoneSubmissionDateCookie(): void {
    const now = new Date();
    this.cookie.setCookie('phoneSubmissionDate', now.toISOString());
  }

  /**
   * Writes the phone capture coupon into its dedicated `phoneCoupon` cookie, which
   * CouponAggregationService reads as a separate source. Kept distinct from the email
   * capture's `emailCoupon` cookie so a phone submission never makes the email capture
   * component treat itself as submitted (which would pop its modal on other pages).
   */
  public setPhoneCouponCookie(): void {
    this.cookie.setCookie('phoneCoupon', this.coupon);
  }

  public checkAndCleanExpiredSubmission(): void {
    if (this.hasUrgencyData && this.submissionDate) {
      const expirationDate = new Date(this.submissionDate);
      expirationDate.setDate(expirationDate.getDate() + this.effectiveCountdownDays);

      if (new Date() > expirationDate) {
        this.cookie.deleteCookie('phoneCaptureSubmitted');
        this.cookie.deleteCookie('phoneSubmissionDate');
        this.cookie.deleteCookie('phoneCoupon');
        this.isSubmited = false;
        this.submissionDate = null;
        this.updateUrgencyMessage();
      }
    }
  }

  // Brand-scope the minimize cookie so state from one brand doesn't bleed into another
  // when they share a cookie scope (matches the dismiss-key brand-scoping convention).
  private get phoneMinimizeCookieKey(): string {
    return `phoneMinimize-${this.brandId}`;
  }

  public getPhoneMinimizeCookie(): void {
    const cookieValue = this.cookie.getCookie(this.phoneMinimizeCookieKey);
    this.isMinimized = cookieValue === 'true';
  }

  public setPhoneMinimizeCookie(state: string): void {
    this.cookie.setCookie(this.phoneMinimizeCookieKey, state);
  }

  handleScroll(): void {
    const paramOveride = this.route.snapshot.queryParamMap?.get('showCapture');
    if (paramOveride && paramOveride === 'false') {
      this.setPhoneCapture(false, this.stickyPhoneOverride);
    } else {
      this.setPhoneCapture(undefined, this.stickyPhoneOverride);
    }
  }

  isSaleActive(): boolean {
    if (this.data?.saleStart && this.data?.saleEnd) {
      const saleStartTime = this.saleStart.getTime();
      const saleEndTime = this.saleEnd.getTime();
      if (this.now < saleEndTime.valueOf() && this.now > saleStartTime.valueOf()) {
        return true;
      }
    }
    return false;
  }

  setPhoneCapture(overide?: boolean, stickyPhoneOverride?: number): void {
    const pixelsOverride = stickyPhoneOverride ? stickyPhoneOverride : 100;
    this.saleActive = this.isSaleActive();
    const minimizedValue = this.cookie.getCookie(this.phoneMinimizeCookieKey);
    if (overide !== undefined) {
      this.showPhoneCapture = overide;
      this.syncModalState();
      return;
    }
    const dismissed = this.storageService.get(`${this.dismissedString}-${this.brandId}`) === 'true';
    const scrollY = this.isBrowser && this.window ? this.window.scrollY : 0;

    this.showPhoneCapture =
      !this.ambassador &&
      !this.isLoggedIn &&
      !this.saleActive &&
      this.data?.showStickyPhoneCapture &&
      !dismissed &&
      (stickyPhoneOverride === 0 || scrollY > pixelsOverride);

    this.syncModalState();

    if (!this.isModalMode && minimizedValue !== 'true' && this.isBrowser && this.window && this.window.scrollY < 100) {
      this.isMinimized = false;
    }
  }

  async submit(): Promise<void> {
    const formValue = this.phoneForm.value as { phoneNumber: string };
    const rawPhone = String(formValue.phoneNumber ?? '');
    const digitsOnly = rawPhone.replace(/\D/g, '');
    const e164 = this.phoneValidation.normalizeToE164(rawPhone);

    if (!e164) {
      this.isPhoneInvalid = true;

      this.elementTracking.trackFormError(
        'page_phonecapture_signup',
        'phone_form',
        'footer',
        'VALIDATION_ERROR',
        'Please enter a valid phone number',
        { phone_last4: this.maskPhone(digitsOnly) },
      );

      return;
    }
    this.isPhoneInvalid = false;

    const e164Digits = e164.replace('+', '');

    const listInfo = this.phoneListInfo;
    if (!listInfo) {
      // Brand has no configured Iterable list — silently no-op rather than throw on submit.
      this.elementTracking.trackFormError(
        'page_phonecapture_signup',
        'phone_form',
        'footer',
        'CONFIG_ERROR',
        `No Iterable list configured for brand ${this.brandId}`,
        { phone_last4: this.maskPhone(e164Digits) },
      );
      return;
    }

    const placeholderEmail = `${e164Digits}@placeholder.email`;
    const iterableData: FormPayload = {
      listId: listInfo.listId,
      subscribers: [
        {
          dataFields: {
            brandId: this.brandId,
            phoneNumber: e164,
          },
          email: placeholderEmail,
        },
      ],
      type: listInfo.type,
    };

    const elementValue = {
      subscription_list_id: listInfo.listId,
      phone_last4: this.maskPhone(e164Digits),
      brand_id: this.brandId,
      has_urgency: this.hasUrgencyData,
      ...(this.hasUrgencyData &&
        this.effectiveCountdownDays !== undefined && { countdown_days: this.effectiveCountdownDays }),
      ...(this.hasUrgencyData &&
        this.effectivePercentage !== undefined && { percentage_off: this.effectivePercentage }),
    };

    try {
      await this.iterable.addToMailList(iterableData, this.brandId);

      this.isSubmited = true;
      this.submissionDate = new Date();
      this.setPhoneSubmittedCookie();
      this.setPhoneSubmissionDateCookie();
      // Persist the coupon so it rides onto checkout links via CouponAggregationService.
      this.setPhoneCouponCookie();

      this.elementTracking.trackFormSubmit('page_phonecapture_signup', 'phone_form', 'footer', elementValue, true);

      // Broadcast the coupon so pricing/checkout components re-resolve their links immediately.
      // Small delay mirrors email capture — ensures the cookie is set before the refresh fires.
      setTimeout(() => {
        this.pricingService.sendCouponEvent(this.coupon);
      }, 100);

      void this.setOffConfetti();
      this.updateUrgencyMessage();
    } catch (error) {
      this.isSubmited = false;

      const errorMessage =
        error instanceof Error ? error.message : 'There was an issue with your subscription. Please try again later.';
      this.elementTracking.trackFormError(
        'page_phonecapture_signup',
        'phone_form',
        'footer',
        'SUBMISSION_ERROR',
        errorMessage,
        elementValue,
      );
    }
  }

  /** Returns last 4 digits prefixed with the masked area code/exchange. Empty input → empty string. */
  private maskPhone(digits: string): string {
    if (!digits) return '';
    return digits.length > 4 ? `***-***-${digits.slice(-4)}` : '***';
  }

  /** Prefixes a protocol-less brand url (e.g. "www.aceable.com") with https://. Empty input → empty string. */
  private toAbsoluteUrl(url?: string): string {
    if (!url) return '';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  }

  async setOffConfetti(): Promise<void> {
    if (!this.isBrowser) return;
    this.displayConfetti = true;

    // Dynamic-import canvas-confetti so the ~10 KB animation library only loads on successful submit.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const confettiModule = await import('canvas-confetti');

    setTimeout(() => {
      const canvas = this.document.getElementById('phone-capture-canvas') as HTMLCanvasElement | null;
      if (!canvas) return;

      const createFn = (
        confettiModule as unknown as { create: (el: HTMLCanvasElement) => (opts: object) => Promise<null> }
      ).create;

      void createFn(canvas)({
        shapes: ['square'],
        particleCount: 100,
        spread: 40,
        scalar: 0.5,
        origin: { y: 1, x: 0.5 },
      });
    }, 500);

    setTimeout(() => {
      this.displayConfetti = false;
    }, 5000);
  }

  toggleSize(): void {
    this.isMinimized = !this.isMinimized;
    if (this.isModalMode) {
      this.modalActive = false;
      this.modalDismissed = true;
      this.snapTransition();
      return;
    }
    this.setPhoneMinimizeCookie(this.isMinimized ? 'true' : 'false');
  }

  close(): void {
    this.storageService.set(`${this.dismissedString}-${this.brandId}`, 'true');
    this.showPhoneCapture = false;
    // Tear the scrim down with the banner. Without this, the `@if (modalActive)` backdrop
    // lingers until the next scroll re-runs syncModalState(). Mirrors toggleSize().
    this.modalActive = false;
    this.modalDismissed = true;
  }

  private syncModalState(): void {
    if (this.isModalMode && this.showPhoneCapture && !this.modalDismissed && !this.isSubmited) {
      this.modalActive = true;
    }
    if (!this.showPhoneCapture) {
      this.modalActive = false;
    }
  }

  private snapTransition(): void {
    this.suppressTransition = true;
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.suppressTransition = false;
        });
      });
    } else {
      this.suppressTransition = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
